import {
  AdminListValue,
  ListValueStatus,
  ManagedListType,
  UpsertListValuePayload,
} from '@/types/list-value.types';

export type ListValueFormState = {
  key: string;
  label_fi: string;
  label_sv: string;
  label_en: string;
  label_de: string;
  description: string;
  notes: string;
  linked_jira_issue: string;
  status: ListValueStatus;
  category_id: string;
  category: string;
  icon_id: string;
  display_order: string;
};

export type FormErrors = Partial<Record<keyof ListValueFormState, string>>;

export const DEFAULT_MANAGED_LIST_TYPE = ManagedListType.HARBOR_CATEGORY;

export const LIST_VALUE_MANAGEMENT_TYPES: ManagedListType[] = [
  ManagedListType.HARBOR_CATEGORY,
  ManagedListType.HARBOR_FACILITY,
  ManagedListType.NAVIGATIONAL_AID,
  ManagedListType.FAIRWAY_CATEGORY,
  ManagedListType.WATERWAY_SIGN,
  ManagedListType.COASTAL_AREA,
  ManagedListType.MOORING_STYLE,
  ManagedListType.POI_CATEGORY,
  ManagedListType.POI_SUBTYPE,
  ManagedListType.SERVICE_CATEGORY,
  ManagedListType.SERVICE_SUB_CATEGORY,
];

export const LIST_VALUE_ICON_TYPES: ManagedListType[] = [
  ManagedListType.HARBOR_CATEGORY,
  ManagedListType.HARBOR_FACILITY,
  ManagedListType.NAVIGATIONAL_AID,
  ManagedListType.SERVICE_SUB_CATEGORY,
];

export const LIST_VALUE_DISPLAY_ORDER_TYPES: ManagedListType[] = [
  ManagedListType.HARBOR_CATEGORY,
  ManagedListType.NAVIGATIONAL_AID,
  ManagedListType.SERVICE_SUB_CATEGORY,
  ManagedListType.FAIRWAY_CATEGORY,
  ManagedListType.WATERWAY_SIGN,
];

// Types whose `key` is hardcoded into frontend map/icon logic (e.g. waterway
// sign vlm_* icon derivation, navigational aid icon mapping) — editing the
// key after creation would silently break that source-data mapping.
export const LIST_VALUE_KEY_LOCKED_TYPES: ManagedListType[] = [
  ManagedListType.NAVIGATIONAL_AID,
  ManagedListType.WATERWAY_SIGN,
];

export const LIST_TYPE_CONFIG: Record<
  ManagedListType,
  { title: string; singularTitle: string; blurb: string; emptyLabel: string }
> = {
  [ManagedListType.MOORING_STYLE]: {
    title: 'Mooring Styles',
    singularTitle: 'mooring style',
    blurb:
      'Manage the dynamic mooring style registry used across berth management workflows.',
    emptyLabel: 'No mooring styles found.',
  },
  [ManagedListType.COASTAL_AREA]: {
    title: 'Coastal Areas',
    singularTitle: 'coastal area',
    blurb:
      'Manage the dynamic coastal area registry used in harbor forms and filtering.',
    emptyLabel: 'No coastal areas found.',
  },
  [ManagedListType.POI_CATEGORY]: {
    title: 'POI Categories',
    singularTitle: 'POI category',
    blurb:
      'Manage the point-of-interest categories used to organise POI sub types.',
    emptyLabel: 'No POI categories found.',
  },
  [ManagedListType.POI_SUBTYPE]: {
    title: 'POI Sub Types',
    singularTitle: 'POI sub type',
    blurb: 'Manage the dynamic point-of-interest sub type registry.',
    emptyLabel: 'No POI sub types found.',
  },
  [ManagedListType.SERVICE_SUB_CATEGORY]: {
    title: 'Service Sub Categories',
    singularTitle: 'service sub category',
    blurb:
      'Manage the dynamic service sub category registry used in service management.',
    emptyLabel: 'No service sub categories found.',
  },
  [ManagedListType.SERVICE_CATEGORY]: {
    title: 'Service Categories',
    singularTitle: 'service category',
    blurb:
      'Manage the dynamic service category registry used to organise service sub categories.',
    emptyLabel: 'No service categories found.',
  },
  [ManagedListType.HARBOR_CATEGORY]: {
    title: 'Harbor Categories',
    singularTitle: 'harbor category',
    blurb:
      'Manage the dynamic harbor category registry used in harbor forms and detail views.',
    emptyLabel: 'No harbor categories found.',
  },
  [ManagedListType.HARBOR_FACILITY]: {
    title: 'Harbor Facilities',
    singularTitle: 'harbor facility',
    blurb:
      'Manage the dynamic facility list used in harbor facilities and icon labeling.',
    emptyLabel: 'No harbor facilities found.',
  },
  [ManagedListType.NAVIGATIONAL_AID]: {
    title: 'Navigational Aids',
    singularTitle: 'navigational aid',
    blurb:
      'Manage the dynamic navigational aid registry used for icon labeling and routing-related references.',
    emptyLabel: 'No navigational aids found.',
  },
  [ManagedListType.FAIRWAY_CATEGORY]: {
    title: 'Fairway Categories',
    singularTitle: 'fairway category',
    blurb:
      'Manage fairway legend categories and their display order on the map legend.',
    emptyLabel: 'No fairway categories found.',
  },
  [ManagedListType.WATERWAY_SIGN]: {
    title: 'Waterway Signs',
    singularTitle: 'waterway sign',
    blurb:
      'Manage waterway sign legend entries and their display order on the map legend.',
    emptyLabel: 'No waterway signs found.',
  },
};

export const EMPTY_FORM: ListValueFormState = {
  key: '',
  label_fi: '',
  label_sv: '',
  label_en: '',
  label_de: '',
  description: '',
  notes: '',
  linked_jira_issue: '',
  status: ListValueStatus.APPROVED,
  category_id: '',
  category: '',
  icon_id: '',
  display_order: '',
};

export const getBadgeClass = (status: ListValueStatus) => {
  switch (status) {
    case ListValueStatus.APPROVED:
      return 'badge badge-success';
    case ListValueStatus.DRAFT:
      return 'badge badge-secondary';
    case ListValueStatus.DEPRECATED:
      return 'badge badge-warning';
    default:
      return 'badge badge-light';
  }
};

export const getStatusLabel = (status: ListValueStatus) => {
  switch (status) {
    case ListValueStatus.DRAFT:
      return 'Draft';
    case ListValueStatus.APPROVED:
      return 'Approved';
    case ListValueStatus.DEPRECATED:
      return 'Deprecated';
    default:
      return status;
  }
};

export const toFormState = (value?: AdminListValue): ListValueFormState => ({
  key: value?.key || '',
  label_fi: value?.label_fi || '',
  label_sv: value?.label_sv || '',
  label_en: value?.label_en || '',
  label_de: value?.label_de || '',
  description: value?.description || '',
  notes: value?.notes || '',
  linked_jira_issue: value?.linked_jira_issue || '',
  status: value?.status || ListValueStatus.APPROVED,
  category_id: value?.category_id || '',
  category: value?.category || '',
  icon_id:
    value?.icon_id === null || value?.icon_id === undefined
      ? ''
      : String(value.icon_id),
  display_order:
    value?.display_order === null || value?.display_order === undefined
      ? ''
      : String(value.display_order),
});

export const sanitizePayload = (
  form: ListValueFormState,
  type: ManagedListType
): UpsertListValuePayload => {
  const normalizedIconId = form.icon_id.trim();
  const payload: UpsertListValuePayload = {
    key: form.key.trim(),
    label_fi: form.label_fi.trim(),
    label_sv: form.label_sv.trim(),
    label_en: form.label_en.trim(),
    label_de: form.label_de.trim(),
    status: form.status,
    description: form.description.trim() || undefined,
    notes: form.notes.trim() || undefined,
    linked_jira_issue: form.linked_jira_issue.trim() || undefined,
    category_id: form.category_id.trim() || undefined,
    category: form.category.trim() || undefined,
    icon_id: normalizedIconId
      ? Number.parseInt(normalizedIconId, 10)
      : undefined,
  };

  if (LIST_VALUE_DISPLAY_ORDER_TYPES.includes(type)) {
    const trimmed = form.display_order.trim();
    payload.display_order = trimmed ? Number.parseInt(trimmed, 10) : null;
  }

  return payload;
};

export const validateForm = (
  form: ListValueFormState,
  type: ManagedListType
): FormErrors => {
  const errors: FormErrors = {};

  if (!form.key.trim()) errors.key = 'Key is required.';
  if (form.key.trim().length > 100)
    errors.key = 'Key must be at most 100 characters.';
  if (!form.label_fi.trim()) errors.label_fi = 'Finnish label is required.';
  if (!form.label_sv.trim()) errors.label_sv = 'Swedish label is required.';
  if (!form.label_en.trim()) errors.label_en = 'English label is required.';
  if (!form.label_de.trim()) errors.label_de = 'German label is required.';
  if (form.linked_jira_issue.trim().length > 100) {
    errors.linked_jira_issue =
      'Linked Jira issue must be at most 100 characters.';
  }
  if (type === ManagedListType.POI_SUBTYPE && !form.category_id.trim()) {
    errors.category_id = 'POI category is required.';
  }
  if (type === ManagedListType.SERVICE_SUB_CATEGORY && !form.category.trim()) {
    errors.category = 'Service category is required.';
  }
  if (
    LIST_VALUE_ICON_TYPES.includes(type) &&
    form.icon_id.trim() &&
    Number.isNaN(Number.parseInt(form.icon_id, 10))
  ) {
    errors.icon_id = 'Icon must be a valid selection.';
  }
  if (
    LIST_VALUE_DISPLAY_ORDER_TYPES.includes(type) &&
    form.display_order.trim()
  ) {
    const parsed = Number.parseInt(form.display_order.trim(), 10);
    if (
      Number.isNaN(parsed) ||
      parsed < 1 ||
      String(parsed) !== form.display_order.trim()
    ) {
      errors.display_order = 'Display order must be a positive whole number.';
    }
  }

  return errors;
};

export const isManagedListType = (
  value: string | null | undefined
): value is ManagedListType =>
  Object.values(ManagedListType).includes(value as ManagedListType);

export const isListValueManagementType = (
  value: string | null | undefined
): value is ManagedListType =>
  LIST_VALUE_MANAGEMENT_TYPES.includes(value as ManagedListType);

export const supportsListValueIcon = (
  value: ManagedListType | null | undefined
): value is ManagedListType =>
  LIST_VALUE_ICON_TYPES.includes(value as ManagedListType);

export const supportsDisplayOrder = (
  value: ManagedListType | null | undefined
): value is ManagedListType =>
  LIST_VALUE_DISPLAY_ORDER_TYPES.includes(value as ManagedListType);

export const isKeyLockedListType = (
  value: ManagedListType | null | undefined
): value is ManagedListType =>
  LIST_VALUE_KEY_LOCKED_TYPES.includes(value as ManagedListType);
